import { useCallback } from 'react';
import {
  useAgentsQuery,
  useCreateSessionMutation,
  useSendMessageMutation,
} from '../api';
import { AgentForgeApiError } from '../api/http';
import { toOptimisticAttachments } from '../helpers';
import { useAgentForgeStore } from '../stores';
import type { QueuedTurnSelection } from '../stores/agentForge/types';
import type { BlockOutput } from '../types';
import { reserveFreshSession } from './freshSessionReservation';
import { createQueuedTurn } from './queuedTurn';
import {
  beginTurnSubmission,
  finishTurnSubmission,
} from './turnSubmissionRegistry';
import type { ChatSubmit, ChatSubmitOptions } from './useChatSubmit.types';

const isStaleSessionError = (err: unknown): boolean =>
  err instanceof AgentForgeApiError && err.status === 404;

const isActiveTurnConflict = (err: unknown): boolean =>
  err instanceof AgentForgeApiError &&
  err.status === 409 &&
  err.errors.some((message) =>
    /response is already being generated|turn (?:is )?already running/i.test(
      message
    )
  );

// A network disconnect or 5xx can happen after Temporal accepted the turn but
// before the 202 reached the browser. Treat only explicit 4xx responses as a
// definite rejection; ambiguous failures stay persisted and are reconciled by
// GET turn-status.
const isDefinitiveSendFailure = (err: unknown): boolean =>
  err instanceof AgentForgeApiError &&
  err.status >= 400 &&
  err.status < 500 &&
  !isActiveTurnConflict(err);

const DEFAULT_AGENT_SLUG = 'pipeline-manager';

// True while the active session has a turn in flight. Sends are blocked for
// its whole duration: the store keys streaming state (draft, sub-steps,
// status) by session only, so a second concurrent turn on the same session
// makes the first stream_done wipe the second turn's in-progress UI.
// Serializing turns here — the one choke point every send path (composer,
// chips, clarify forms) goes through — is what keeps that state coherent.
const sessionStreaming = (
  s: ReturnType<typeof useAgentForgeStore.getState>,
  sessionId = s.activeSessionId
): boolean => {
  if (!sessionId) return false;
  const tab = s.openTabs.find((t) => t.sessionId === sessionId);
  return (
    !!s.activeTurnsBySession[sessionId] ||
    tab?.status === 'streaming' ||
    s.streamingDraft?.sessionId === sessionId
  );
};

const blockToolCallId = (block: BlockOutput): string | null => {
  const data = block.data as { toolCallId?: unknown };
  return typeof data.toolCallId === 'string' ? data.toolCallId : null;
};

// Snapshot the active card selection at send time, but only if that card is
// visible in the active session. The backend independently verifies the same
// coordinates against persisted tool output.
const currentSelections = (
  sessionId?: string | null
): QueuedTurnSelection[] => {
  const state = useAgentForgeStore.getState();
  const targetSessionId = sessionId ?? state.activeSessionId;
  if (!targetSessionId) return [];

  const visibleToolCalls = new Set<string>();
  for (const message of state.messagesBySession[targetSessionId] ?? []) {
    if (message.role !== 'assistant') continue;
    for (const output of message.outputs) {
      const id = blockToolCallId(output);
      if (id) visibleToolCalls.add(id);
    }
  }
  if (state.streamingDraft?.sessionId === targetSessionId) {
    for (const output of state.streamingDraft.outputs) {
      const id = blockToolCallId(output);
      if (id) visibleToolCalls.add(id);
    }
  }

  const nonEmpty = Object.entries(state.selectionByBlock).filter(
    ([toolCallId, ids]) => visibleToolCalls.has(toolCallId) && ids.length > 0
  );
  const active = visibleToolCalls.has(state.activeSelectionBlock ?? '')
    ? state.activeSelectionBlock
    : null;
  const target = active ?? (nonEmpty.length === 1 ? nonEmpty[0][0] : null);
  if (!target) return [];
  const ids = (state.selectionByBlock[target] ?? [])
    .map((id) => Number(id))
    .filter((id) => Number.isSafeInteger(id) && id > 0)
    .slice(0, 50);
  return ids.length > 0 ? [{ tool_call_id: target, ids }] : [];
};

// Composes "resolve-or-create session → append user msg → mark streaming".
// Each consumer wires the returned `send` into Composer.onSubmit. The real
// path POSTs to `/sessions/{id}/messages` and flips the tab status to
// `streaming`. The reply lands via V1's WebSocket (`useAgentForgeStream`)
// and the canonical record is fetched by `useAgentForgeMessages` after
// `stream_done`.
//
// `send` returns the sessionId so callers can navigate to a per-session
// route after a fresh session is created. Returns `null` on empty input
// or session-resolution failure.
//
// `activeSessionId` is read via `useAgentForgeStore.getState()` inside the
// callback (not via a closing selector) so callers that openTab a
// pre-warmed session synchronously *before* calling `send` see the new
// active session in the same tick — without the callback having to
// recreate first.
export const useChatSubmit = (): ChatSubmit => {
  const openTab = useAgentForgeStore((s) => s.openTab);
  const migrateSession = useAgentForgeStore((s) => s.migrateSession);
  const setMode = useAgentForgeStore((s) => s.setMode);
  const appendUserMessage = useAgentForgeStore((s) => s.appendUserMessage);
  const removeMessage = useAgentForgeStore((s) => s.removeMessage);
  const clearQueuedMessage = useAgentForgeStore((s) => s.clearQueuedMessage);
  const upsertActiveTurn = useAgentForgeStore((s) => s.upsertActiveTurn);
  const resolveActiveTurn = useAgentForgeStore((s) => s.resolveActiveTurn);
  const setActiveSubmission = useAgentForgeStore((s) => s.setActiveSubmission);
  const clearActiveSubmission = useAgentForgeStore(
    (s) => s.clearActiveSubmission
  );

  const agentsQuery = useAgentsQuery();
  const createSessionMutation = useCreateSessionMutation();
  const sendMessage = useSendMessageMutation();

  // Reactive mirror of the send() guard below, so consumers' disabled
  // states track the stream, not just the POST.
  const isStreaming = useAgentForgeStore((state) => sessionStreaming(state));

  const send = useCallback(
    async (
      text: string,
      files?: File[],
      opts?: ChatSubmitOptions
    ): Promise<string | null> => {
      const queuedSubmission = opts?.queuedTurn;
      const trimmed = (queuedSubmission?.text ?? text).trim();
      const attachments = queuedSubmission
        ? [...queuedSubmission.files]
        : (files ?? []);
      if (trimmed.length === 0 && attachments.length === 0) return null;

      // Optimistic bubble text for a file-only submit (no typed message).
      const optimisticText =
        queuedSubmission?.displayText ??
        (trimmed.length > 0
          ? trimmed
          : `Uploaded ${attachments.map((f) => f.name).join(', ')}`);
      const optimisticAttachments = toOptimisticAttachments(attachments);
      const initialState = useAgentForgeStore.getState();
      const requestedSessionId =
        opts?.targetSessionId ?? initialState.activeSessionId;
      const selections = (
        queuedSubmission?.selections ??
        opts?.selectionSnapshot ??
        currentSelections(requestedSessionId)
      ).map((selection) => ({
        tool_call_id: selection.tool_call_id,
        ids: [...selection.ids],
      }));
      const newSubmission = () =>
        createQueuedTurn(trimmed, optimisticText, attachments, selections);
      const enqueue = (
        sessionId: string,
        submission = newSubmission()
      ): void => {
        useAgentForgeStore.getState().setQueuedMessage(sessionId, submission);
      };

      // One turn at a time per session (read fresh state — the callback may
      // outlive the render that created it). A text-only submit during a
      // running turn QUEUES instead of dropping: it auto-sends when the reply
      // finishes (useQueuedSendDispatcher). File handles are retained in the
      // in-memory queue so a 409 race cannot silently discard an attachment.
      {
        const state = useAgentForgeStore.getState();
        if (requestedSessionId && sessionStreaming(state, requestedSessionId)) {
          enqueue(requestedSessionId, queuedSubmission ?? newSubmission());
          return requestedSessionId;
        }
        // Once a session has parked work, later composer sends join that FIFO.
        // They must not jump ahead merely because the failed turn unlocked the
        // composer. Internal dispatch/retry calls carry queuedTurn or silent.
        if (
          requestedSessionId &&
          !queuedSubmission &&
          !opts?.silent &&
          (state.queuedBySession[requestedSessionId]?.length ?? 0) > 0
        ) {
          enqueue(requestedSessionId);
          return requestedSessionId;
        }
      }

      const activeSessionId = requestedSessionId;

      const startFreshSession = async (
        openImmediately = true
      ): Promise<string | null> => {
        const agents = agentsQuery.data ?? [];
        const agent =
          agents.find((a) => a.slug === DEFAULT_AGENT_SLUG) ?? agents[0];
        if (!agent) return null;
        const session = await createSessionMutation.mutateAsync({
          agent_id: agent.id,
          title: optimisticText,
        });
        if (openImmediately) {
          openTab({
            sessionId: session.id,
            title: session.title || optimisticText,
            status: 'idle',
            createdAt: session.created_at,
          });
          setMode('floating');
        }
        return session.id;
      };

      const stampOptimistic = (
        id: string,
        turnId: string,
        reuseExistingBubble = opts?.silent === true
      ): string | null => {
        let optimisticMessageId: string | null = null;
        // silent = retry of a message whose bubble is already on screen —
        // re-appending would duplicate it.
        if (!reuseExistingBubble) {
          optimisticMessageId = appendUserMessage(
            id,
            optimisticText,
            optimisticAttachments
          );
        } else {
          const messages =
            useAgentForgeStore.getState().messagesBySession[id] ?? [];
          for (let index = messages.length - 1; index >= 0; index -= 1) {
            if (messages[index].role === 'user') {
              optimisticMessageId = messages[index].id;
              break;
            }
          }
        }
        // setTabStatus is a no-op when the session has no tab entry — and
        // sessions opened via setActive (deep link, history list) never
        // called openTab. The turn then rendered with NO thinking indicator
        // until the first WS frame arrived ("the activity is lost"). Upsert
        // the tab before stamping it so isAwaitingReply flips immediately.
        const tabs = useAgentForgeStore.getState().openTabs;
        if (!tabs.some((t) => t.sessionId === id)) {
          openTab({
            sessionId: id,
            title: optimisticText,
            status: 'idle',
            createdAt: new Date().toISOString(),
          });
        }
        // Persist a submitting turn before the POST starts. If the page reloads
        // while routing is still inside that request, the recovery hook can
        // discover the workflow by session instead of losing the spinner.
        upsertActiveTurn(id, {
          turnId,
          identityMode: 'probing',
          phase: 'submitting',
        });
        return optimisticMessageId;
      };

      const recordAccepted = (
        id: string,
        clientTurnId: string,
        accepted: {
          turn_id?: string;
          workflow_id: string;
          run_id?: string;
        }
      ) => {
        const current = useAgentForgeStore.getState().activeTurnsBySession[id];
        // A delayed acknowledgement for turn A must never overwrite turn B.
        // Status/hydration may also have completed A before its 202 arrived.
        if (!current || current.turnId !== clientTurnId) return;
        // An echoed but different UUID is a protocol mismatch, not evidence
        // that this is a legacy server. Keep probing the UUID we actually sent.
        if (accepted.turn_id && accepted.turn_id !== clientTurnId) return;
        if (current.workflowId && current.workflowId !== accepted.workflow_id)
          return;

        const exact =
          current.identityMode === 'exact' || accepted.turn_id === clientTurnId;
        const phase =
          current.phase === 'cancelling' || current.phase === 'finalizing'
            ? current.phase
            : 'running';
        upsertActiveTurn(id, {
          phase,
          // A current backend must echo the client UUID. Missing/mismatched
          // identity on an otherwise compatible acknowledgement is treated as
          // legacy so Stop never targets an unverified exact record.
          turnId: exact ? clientTurnId : undefined,
          identityMode: exact ? 'exact' : 'legacy',
          workflowId: accepted.workflow_id,
          ...(accepted.run_id ? { runId: accepted.run_id } : {}),
        });
      };

      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      let sessionId = activeSessionId;
      let freshSessionRelease: (() => void) | undefined;
      if (!sessionId) {
        const lease = reserveFreshSession(startFreshSession);
        freshSessionRelease = lease.release;
        try {
          sessionId = await lease.session;
        } catch {
          lease.release();
          return null;
        }
        if (!sessionId) {
          lease.release();
          return null;
        }
        // A same-tick send joined the owner's fresh-session request. It must
        // become the next turn on that one session, never create/post a second
        // concurrent turn.
        if (!lease.owner) {
          enqueue(sessionId, queuedSubmission ?? newSubmission());
          return sessionId;
        }
      }

      const submission = queuedSubmission ?? newSubmission();
      const turnId = submission.id;
      setActiveSubmission(sessionId, submission);
      let optimisticMessageId: string | null = null;
      try {
        optimisticMessageId = stampOptimistic(sessionId, turnId);
      } finally {
        // Keep the fresh-session reservation until the owner has synchronously
        // stamped its active turn. This closes the create-response microtask
        // gap for a third concurrent sender.
        freshSessionRelease?.();
      }

      const setUploadProgress = useAgentForgeStore.getState().setUploadProgress;
      const stillOwnsTurn = (id: string, expectedTurnId: string): boolean =>
        useAgentForgeStore.getState().activeTurnsBySession[id]?.turnId ===
        expectedTurnId;
      const clearUploadProgressUnlessSuperseded = (
        id: string,
        expectedTurnId: string
      ) => {
        const current = useAgentForgeStore.getState().activeTurnsBySession[id];
        if (!current || current.turnId === expectedTurnId) {
          setUploadProgress(id, expectedTurnId, null);
        }
      };
      const submissionController = beginTurnSubmission(sessionId, turnId);
      try {
        const accepted = await sendMessage.mutateAsync({
          sessionId,
          turnId,
          message: trimmed,
          timezone: tz,
          selections,
          files: attachments,
          signal: submissionController.signal,
          onUploadProgress:
            attachments.length > 0
              ? (progress) => setUploadProgress(sessionId, turnId, progress)
              : undefined,
        });
        // A very fast workflow can complete via WS/status while the 202 is
        // still crossing the network. Never resurrect a turn that canonical
        // hydration already resolved.
        clearUploadProgressUnlessSuperseded(sessionId, turnId);
        recordAccepted(sessionId, turnId, accepted);
        if (queuedSubmission) clearQueuedMessage(sessionId, submission.id);
        return sessionId;
      } catch (err) {
        clearUploadProgressUnlessSuperseded(sessionId, turnId);
        // Every outcome below belongs to this logical UUID. A delayed failure
        // for turn A must not mutate or recreate a session now running turn B.
        if (!stillOwnsTurn(sessionId, turnId)) return sessionId;
        if (!isStaleSessionError(err)) {
          if (isDefinitiveSendFailure(err)) {
            resolveActiveTurn(sessionId, 'error');
          } else if (isActiveTurnConflict(err)) {
            // Our UUID was definitively rejected. Move its optimistic bubble
            // into the explicit queue before transcript hydration can erase
            // it, preserving text, file handles, and checkbox context.
            if (optimisticMessageId) {
              removeMessage(sessionId, optimisticMessageId);
            }
            enqueue(sessionId, submission);
            clearActiveSubmission(sessionId);
            // This generated UUID was not accepted; discover the session's
            // already-running legacy/latest turn instead of polling a UUID
            // that cannot exist.
            upsertActiveTurn(sessionId, {
              phase: 'recovering',
              turnId: undefined,
              identityMode: 'legacy',
            });
          } else {
            const current =
              useAgentForgeStore.getState().activeTurnsBySession[sessionId];
            upsertActiveTurn(sessionId, {
              phase:
                current?.phase === 'cancelling' ? 'cancelling' : 'recovering',
            });
          }
          return sessionId;
        }
      } finally {
        finishTurnSubmission(sessionId, turnId, submissionController);
      }

      // Stale persisted session — backend doesn't know it. Create a fresh
      // session and migrate every local intent (including FIFO file handles
      // and selection snapshots) before retrying this same logical turn.
      upsertActiveTurn(sessionId, { phase: 'recovering' });
      let recoveredId: string | null = null;
      const recoveryLease = reserveFreshSession(
        () => startFreshSession(false),
        `recover:${sessionId}`
      );
      try {
        recoveredId = await recoveryLease.session;
      } catch {
        recoveryLease.release();
        resolveActiveTurn(sessionId, 'error');
        return null;
      }
      if (!recoveredId) {
        recoveryLease.release();
        resolveActiveTurn(sessionId, 'error');
        return null;
      }
      migrateSession(sessionId, recoveredId);
      if (!recoveryLease.owner) {
        enqueue(recoveredId, submission);
        clearActiveSubmission(recoveredId);
        return recoveredId;
      }
      const recoveredTurnId = submission.id;
      setActiveSubmission(recoveredId, submission);
      let recoveredOptimisticMessageId: string | null = null;
      try {
        recoveredOptimisticMessageId = stampOptimistic(
          recoveredId,
          recoveredTurnId,
          true
        );
      } finally {
        recoveryLease.release();
      }
      const recoveredSubmissionController = beginTurnSubmission(
        recoveredId,
        recoveredTurnId
      );
      try {
        const accepted = await sendMessage.mutateAsync({
          sessionId: recoveredId,
          turnId: recoveredTurnId,
          message: trimmed,
          timezone: tz,
          selections,
          files: attachments,
          signal: recoveredSubmissionController.signal,
          onUploadProgress:
            attachments.length > 0
              ? (progress) =>
                  setUploadProgress(recoveredId, recoveredTurnId, progress)
              : undefined,
        });
        clearUploadProgressUnlessSuperseded(recoveredId, recoveredTurnId);
        recordAccepted(recoveredId, recoveredTurnId, accepted);
        if (queuedSubmission) clearQueuedMessage(recoveredId, submission.id);
      } catch (err) {
        clearUploadProgressUnlessSuperseded(recoveredId, recoveredTurnId);
        if (!stillOwnsTurn(recoveredId, recoveredTurnId)) return recoveredId;
        if (isDefinitiveSendFailure(err)) {
          resolveActiveTurn(recoveredId, 'error');
        } else if (isActiveTurnConflict(err)) {
          if (recoveredOptimisticMessageId) {
            removeMessage(recoveredId, recoveredOptimisticMessageId);
          }
          enqueue(recoveredId, submission);
          clearActiveSubmission(recoveredId);
          upsertActiveTurn(recoveredId, {
            phase: 'recovering',
            turnId: undefined,
            identityMode: 'legacy',
          });
        } else {
          const current =
            useAgentForgeStore.getState().activeTurnsBySession[recoveredId];
          upsertActiveTurn(recoveredId, {
            phase:
              current?.phase === 'cancelling' ? 'cancelling' : 'recovering',
          });
        }
      } finally {
        finishTurnSubmission(
          recoveredId,
          recoveredTurnId,
          recoveredSubmissionController
        );
      }
      return recoveredId;
    },
    [
      openTab,
      migrateSession,
      setMode,
      appendUserMessage,
      removeMessage,
      clearQueuedMessage,
      upsertActiveTurn,
      resolveActiveTurn,
      setActiveSubmission,
      clearActiveSubmission,
      agentsQuery.data,
      createSessionMutation,
      sendMessage,
    ]
  );

  return {
    send,
    isSending:
      createSessionMutation.isPending || sendMessage.isPending || isStreaming,
  };
};
